import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MarkdownText from '../MarkdownText/MarkdownText';
import styles from './productContainer.module.css';
import { abbreviateNumber, convertMonthsToDuration } from '../../helpers';
import { StaticRoutes } from '../../Router';
import { ACTION_ENUM } from '../../helpers/productConfig';
import { useTealium } from '../../tealium/useTealium';

export const ProductContainer = ({
  isSelected,
  updateSelectedProduct,
  showProductCriteria,
  showApplyButton,
  productName,
  productId,
  productCode,
  partnerCode,
  productDescription,
  businessSize,
  annualTurnover,
  monthlyTurnover,
  tradingHistory,
  maxFundingAmount,
  minFundingAmount,
  minRepaymentTerm,
  maxRepaymentTerm,
  resourceUrl,
  isRecommended,
  ctaLabel,
  ctaAction,
  ctaDisabled,
  applicationId,
  businessName,
  onCtaClick
}) => {

  const navigate = useNavigate();
  const { track, events } = useTealium();

  const handleClick = () => {

    if (!updateSelectedProduct) {
      return;
    }

    updateSelectedProduct(
      isSelected ? {} : { productId, productCode, partnerCode }
    );
  };

  const handleCtaClick = () => {
    const label = ctaLabel ?? 'Apply Now';
    if (onCtaClick) {
      onCtaClick(label);
    }
    if (ctaAction === ACTION_ENUM.WITHDRAWAL_JOURNEY) {
      track(events.activeProducts.ctaWithdrawalJourney(label, productName));
      navigate(`/accounts/${applicationId}`);
    } else if (ctaAction === ACTION_ENUM.CALLBACK_REQUEST) {
      track(events.activeProducts.ctaCallbackRequest(label, productName));
      navigate(`/accounts/${applicationId}`);
    } else if (ctaAction === ACTION_ENUM.RE_APPLY_JOURNEY) {
      track(events.activeProducts.ctaReApplyJourney(label, productName));
      sessionStorage.setItem('productId', productId);
      navigate(StaticRoutes.FundingApplicationDetails);
    } else if (ctaAction === ACTION_ENUM.NEW_APPLICATION) {
      track(events.activeProducts.ctaNewApplication(label, productName));
      sessionStorage.setItem('productId', productId);
      navigate(StaticRoutes.FundingApplicationDetails);
    } else {
      track(events.activeProducts.ctaNewApplication(label, productName));
      sessionStorage.setItem('productId', productId);
      navigate(StaticRoutes.FundingApplicationDetails);
    }
  };

  const displayTurnover = useMemo(() => {

    if (!annualTurnover && !monthlyTurnover) {
      return null;
    }

    const formattedAnnual = annualTurnover && `R${abbreviateNumber(annualTurnover)} pa`;
    const formattedMonthly = monthlyTurnover && `R${abbreviateNumber(monthlyTurnover)} pm`;

    if (formattedAnnual && formattedMonthly) {
      return `${formattedAnnual} **or** ${formattedMonthly}`;
    }

    return formattedAnnual || formattedMonthly;
  }, [annualTurnover, monthlyTurnover]);

  const displayTradingHistory = useMemo(() => {
    if (!tradingHistory) {
      return 'N/A';
    }
    const duration = convertMonthsToDuration(tradingHistory, 'auto', true);
    return duration ? `Min ${duration}` : 'N/A';
  }, [tradingHistory]);

  const displayFundingLimit = useMemo(() => {

    const minAmt = minFundingAmount ? `R${abbreviateNumber(minFundingAmount)}` : null;
    const maxAmt = maxFundingAmount ? `R${abbreviateNumber(maxFundingAmount)}` : null;

    if (minAmt && maxAmt) {
      return `${minAmt} to ${maxAmt}`;
    }

    if (minAmt) {
      return `${minAmt}`;
    }

    if (maxAmt) {
      return `Up to ${maxAmt}`;
    }

    return '';

  }, [minFundingAmount, maxFundingAmount]);

  const displayRepaymentTerms = useMemo(() => {
    if (minRepaymentTerm && maxRepaymentTerm) {
      return `${minRepaymentTerm} - ${convertMonthsToDuration(maxRepaymentTerm, 'months')}`;
    }

    if (minRepaymentTerm) {
      return `${convertMonthsToDuration(minRepaymentTerm, 'months')}`;
    }

    if (maxRepaymentTerm) {
      return `${convertMonthsToDuration(maxRepaymentTerm, 'months')}`;
    }

    return 'Pay as you practice';

  }, [minRepaymentTerm, maxRepaymentTerm]);

  return (
    <div
      className={`${styles.card} ${isSelected ? styles.cardSelected : styles.cardUnSelected} ${showApplyButton ? styles.innerProductCard : ''}`}
      onClick={handleClick}>
      <div className={styles.productImage}>
        <img className={styles.imageStyle}
          src={resourceUrl}
          alt={productName}
          role='presentation'
        />
      </div>
      <div>
        {isRecommended && (
          <div className={styles.recommendedBadge}>
            RECOMMENDED
          </div>
        )}
        <div>
          <span className={styles.title}>{productName}</span>
        </div>
        {businessName && (
          <div>
            <span className={styles.businessName}>{businessName}</span>
          </div>
        )}
      </div>
      <div className={styles.desc}>
        <MarkdownText text={productDescription} />
      </div>
      {showProductCriteria === true && (
        <div className={styles.details}>
          <table className={styles.table}>
            <tbody>
              <tr className={styles.tr}>
                <td className={styles.tableTitles}>Business size:</td>
                <td><MarkdownText text={businessSize} /></td>
              </tr>

              <tr className={styles.tr}>
                <td className={styles.tableTitles}>Turnover:</td>
                <td><MarkdownText text={displayTurnover} /></td>
              </tr>

              <tr className={styles.tr}>
                <td className={styles.tableTitles}>Trading history:</td>
                <td>{displayTradingHistory}</td>
              </tr>

              <tr className={styles.tr}>
                <td className={styles.tableTitles}>Funding limit:</td>
                <td>{displayFundingLimit}</td>
              </tr>

              <tr>
                <td className={styles.tableTitles}>Repayment term:</td>
                <td>{displayRepaymentTerms}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {showApplyButton && (
        <div className={styles.applyBtn}>
          <button onClick={handleCtaClick} disabled={ctaDisabled}>{ctaLabel ?? 'Apply Now'}</button>
        </div>
      )}
    </div>
  );
};

ProductContainer.propTypes = {
  isSelected: PropTypes.bool,
  updateSelectedProduct: PropTypes.func,
  productName: PropTypes.string,
  partnerCode: PropTypes.string,
  productId: PropTypes.number,
  productCode: PropTypes.string,
  productDescription: PropTypes.string,
  businessSize: PropTypes.string,
  annualTurnover: PropTypes.number,
  monthlyTurnover: PropTypes.number,
  tradingHistory: PropTypes.number,
  maxFundingAmount: PropTypes.number,
  minFundingAmount: PropTypes.number,
  minRepaymentTerm: PropTypes.number,
  maxRepaymentTerm: PropTypes.number,
  resourceUrl: PropTypes.string,
  showProductCriteria: PropTypes.bool,
  showApplyButton: PropTypes.bool,
  isRecommended: PropTypes.bool,
  ctaLabel: PropTypes.string,
  ctaAction: PropTypes.string,
  ctaDisabled: PropTypes.bool,
  applicationId: PropTypes.string,
  businessName: PropTypes.string,
  onCtaClick: PropTypes.func
};